import { ColumnDef } from "@tanstack/react-table";
import { Link } from "@heroui/link";
import type { TFunction } from "i18next";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

import { getAuditColumns } from "@/features/Audit/getAuditColumns";
import { parseDate } from "@/lib/utils";

import { CheckIcon } from "@radix-ui/react-icons";

export type A7OPCVMUnderlying = {
  id: string;
  type_instrument_du_sous_jacent: string;
  date_de_maturite: string;
  devise_du_sous_jacent: string;
  code_isin_du_sous_jacent: string;
  subordination: string;
  restructuration: string;
  a7_opcvm_issuer_id?: number;
  a7_opcvm_issuer_name?: number;
};

const tableName = "a7_opcvm_underlyings";

export const getUnderlyingsColumns = (
  t: TFunction,
): ColumnDef<A7OPCVMUnderlying>[] => {
  const tTable = (key: string, defaultValue: string) =>
    t(`${tableName}.columns.${key}`, { ns: "tables", defaultValue });
  const tCommon = (key: string, defaultValue: string) =>
    t(`actions.${key}`, { ns: "common", defaultValue });

  return [
    {
      accessorKey: "id",
      header: tTable("id", "ID"),
    },
    {
      accessorKey: "type_instrument_du_sous_jacent",
      header: tTable("type_instrument_du_sous_jacent", "Instrument type"),
    },
    {
      accessorKey: "devise_du_sous_jacent",
      header: tTable("devise_du_sous_jacent", "Currency"),
    },
    {
      accessorKey: "code_isin_du_sous_jacent",
      header: tTable("code_isin_du_sous_jacent", "ISIN"),
    },
    {
      accessorKey: "a7_opcvm_issuer_name",
      header: tTable("a7_opcvm_issuer_name", "Issuer"),
      cell: ({ row }) => {
        return (
          <Link
            href={toWorkspacePath(
              `/admin/a7-opcvm/a7_opcvm_issuers/${row.original.a7_opcvm_issuer_id}`,
            )}
            className="text-xs"
            underline="hover"
          >
            {row.getValue("a7_opcvm_issuer_name")}
          </Link>
        );
      },
    },
    {
      accessorKey: "date_de_maturite",
      header: tTable("date_de_maturite", "Maturity date"),
      cell: ({ row }) => (
        <span className="block">
          {parseDate(row.getValue("date_de_maturite"))}
        </span>
      ),
    },
    {
      accessorKey: "subordination",
      header: () => (
        <div className="text-center">
          {tTable("subordination", "Subordination")}
        </div>
      ),
      cell: ({ getValue }) =>
        getValue() == "YES" && (
          <div className="flex justify-center">
            <CheckIcon />
          </div>
        ),
    },
    {
      accessorKey: "restructuration",
      header: () => (
        <div className="text-center">
          {tTable("restructuration", "Restructuring")}
        </div>
      ),
      cell: ({ getValue }) =>
        getValue() == "YES" && (
          <div className="flex justify-center">
            <CheckIcon />
          </div>
        ),
    },
    {
      id: "actions",
      size: 50,
      cell: ({ row }) => {
        return (
          <Link
            href={`${window.location.pathname}/${row.getValue("id")}`}
            className="text-xs"
            underline="hover"
          >
            {tCommon("edit", "Edit")}
          </Link>
        );
      },
    },
  ];
};

export const getUnderlyingAuditColumns = (t: TFunction) =>
  getAuditColumns<A7OPCVMUnderlying>([
    ...getUnderlyingsColumns(t),
    {
      accessorKey: "a7_opcvm_issuer_id",
      header: t(`${tableName}.columns.a7_opcvm_issuer_id`, {
        ns: "tables",
        defaultValue: "Issuer ID",
      }),
    },
  ]).filter(
    (column) =>
      ("accessorKey" in column
        ? String((column as { accessorKey?: unknown }).accessorKey ?? "")
        : "") !== "a7_opcvm_issuer_name",
  );
